import { i18nService } from '../../../i18n/I18nService';
import { createFormField } from '../../formField';
import { ApiError } from '../../../api/adminApi';
export function t(key, params) {
    return i18nService.t(key, params);
}
// Remaining balances are stored in minutes; the "3 h" / "3 h 30 min" split is
// the same across the panel (sell subscription, remaining hours) and the sale
// email, so all of them read it out of the shared `duration.*` locale keys
// rather than formatting it inline.
export function formatDurationFromMinutes(minutes) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
        ? t('duration.hoursMinutesShort', { hours, minutes: remainingMinutes })
        : t('duration.hoursShort', { hours });
}
export function createField(label, type, value = '') {
    return createFormField('admin', label, type, value);
}
// Every "pick a customer" admin screen (remaining hours, deduct time, booking
// history) renders the same labelled <select>: a disabled placeholder option
// followed by one "Surname Name" option per customer. Extracted here so the
// three screens share one markup contract instead of duplicating it.
export function createCustomerField(labelText, placeholderText, customers) {
    return createSelectField(labelText, placeholderText, customers, (customer) => `${customer.surname} ${customer.name}`);
}
// The labelled-select-with-placeholder markup createCustomerField builds,
// generalized to any list of admin-picked items (a workspace, a plan, …) via
// `optionLabel`. createCustomerField is this with the customer "surname name"
// label baked in — both admin bookings' "Book on behalf" wizard steps
// (bookOnBehalf.ts) use this one directly, since neither picks a customer.
export function createSelectField(labelText, placeholderText, items, optionLabel) {
    const wrapper = document.createElement('label');
    wrapper.className = 'admin__field';
    const label = document.createElement('span');
    label.className = 'admin__field-label';
    label.textContent = labelText;
    const select = document.createElement('select');
    select.className = 'admin__input';
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = placeholderText;
    placeholder.disabled = true;
    placeholder.selected = true;
    select.appendChild(placeholder);
    for (const item of items) {
        const option = document.createElement('option');
        option.value = String(item.id);
        option.textContent = optionLabel(item);
        select.appendChild(option);
    }
    wrapper.append(label, select);
    return { wrapper, select };
}
// The remaining-hours and deduct-time screens both list a customer's
// subscriptions with a "time left" column; deduct-time adds a trailing action
// column via `extraColumn`. Returns the empty-state <p> when there are none, so
// a caller can just drop the result into its (cleared) results container.
export function createSubscriptionsTable(subscriptions, opts) {
    if (subscriptions.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'admin__table-empty';
        empty.textContent = opts.emptyText;
        return empty;
    }
    const table = document.createElement('table');
    table.className = 'admin__table';
    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    const headers = [opts.subscriptionHeader, opts.timeLeftHeader];
    if (opts.extraColumn)
        headers.push(opts.extraColumn.header);
    for (const label of headers) {
        const th = document.createElement('th');
        th.textContent = label;
        headRow.appendChild(th);
    }
    thead.appendChild(headRow);
    table.appendChild(thead);
    const tbody = document.createElement('tbody');
    for (const sub of subscriptions) {
        const row = document.createElement('tr');
        const subscriptionCell = document.createElement('td');
        subscriptionCell.textContent = opts.subscriptionLabel(sub);
        const timeLeftCell = document.createElement('td');
        timeLeftCell.textContent = formatDurationFromMinutes(sub.leftMinutes);
        row.append(subscriptionCell, timeLeftCell);
        if (opts.extraColumn)
            row.appendChild(opts.extraColumn.cell(sub));
        tbody.appendChild(row);
    }
    table.appendChild(tbody);
    return table;
}
// A 401 from any admin API call means the session expired or was revoked —
// every caller responds by dropping back to the login screen.
export function isUnauthorized(err) {
    return err instanceof ApiError && err.status === 401;
}
// A 404 from a save/delete means the row was removed elsewhere (another tab or
// admin) while the modal was open — callers reconcile the UI with reality
// instead of reporting a misleading "couldn't save" error.
export function isNotFound(err) {
    return err instanceof ApiError && err.status === 404;
}
// Title, shared by every screen below the dashboard. The matching back
// button is a separate element (createBackButton) placed at the bottom of
// the screen, mirroring the "back" placement used on the main-site pages.
export function createScreenHeader(titleText) {
    const header = document.createElement('div');
    header.className = 'admin__header';
    const title = document.createElement('h1');
    title.className = 'section-title admin__title';
    title.textContent = titleText;
    header.append(title);
    return header;
}
export function createBackButton(ctx, backLabel) {
    const backButton = document.createElement('button');
    backButton.type = 'button';
    backButton.className = 'admin__back';
    backButton.textContent = backLabel;
    backButton.addEventListener('click', () => ctx.showDashboard());
    return backButton;
}
// A modal overlay (closed by a click outside the modal) appended to
// `ctx.section`, with an empty modal of the given class to fill.
export function createModalOverlay(ctx, modalClassName) {
    const overlay = document.createElement('div');
    overlay.className = 'admin__modal-overlay';
    overlay.addEventListener('click', (event) => {
        if (event.target === overlay)
            overlay.remove();
    });
    const modal = document.createElement('div');
    modal.className = modalClassName;
    overlay.appendChild(modal);
    ctx.section.appendChild(overlay);
    return { overlay, modal };
}
export function createModalButton(className, label, onClick) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.textContent = label;
    button.addEventListener('click', onClick);
    return button;
}
// Shared "Yes/No" confirmation modal used by every destructive/state-changing
// admin action (delete/restore client, cancel subscription, ...). Handles the
// overlay, loading state on the confirm button, and the two API-error cases
// every caller needs: 401 -> back to login, 404 -> the row is already gone
// (deleted/restored elsewhere while the modal was open), so the caller's
// `onDone` runs instead of showing a misleading error. `title` adds a heading
// above the text; `errorTextFor` lets a caller map a specific API error (e.g.
// by its `code`) to its own message instead of the generic `errorText`.
export function openConfirmModal(ctx, opts) {
    const overlay = document.createElement('div');
    overlay.className = 'admin__modal-overlay';
    overlay.addEventListener('click', (event) => {
        if (event.target === overlay)
            overlay.remove();
    });
    const modal = document.createElement('div');
    modal.className = 'admin__modal admin__modal--confirm';
    const text = document.createElement('p');
    text.className = 'admin__modal-confirm-text';
    text.textContent = opts.text;
    const error = document.createElement('p');
    error.className = 'admin__error';
    error.hidden = true;
    const actions = document.createElement('div');
    actions.className = 'admin__modal-confirm-actions';
    const noButton = document.createElement('button');
    noButton.type = 'button';
    noButton.className = 'cta admin__submit';
    noButton.textContent = opts.noLabel;
    noButton.addEventListener('click', () => overlay.remove());
    const yesButton = document.createElement('button');
    yesButton.type = 'button';
    yesButton.className = opts.yesClassName;
    yesButton.textContent = opts.yesLabel;
    yesButton.addEventListener('click', () => {
        error.hidden = true;
        yesButton.disabled = true;
        noButton.disabled = true;
        yesButton.classList.add('is-loading');
        opts
            .action()
            .then(() => {
            overlay.remove();
            opts.onDone();
        })
            .catch((err) => {
            if (isUnauthorized(err)) {
                overlay.remove();
                ctx.showLogin();
                return;
            }
            if (isNotFound(err)) {
                overlay.remove();
                opts.onDone();
                return;
            }
            console.error(opts.logMessage, err);
            error.textContent = opts.errorTextFor?.(err) ?? opts.errorText;
            error.hidden = false;
            yesButton.disabled = false;
            noButton.disabled = false;
            yesButton.classList.remove('is-loading');
        });
    });
    actions.append(noButton, yesButton);
    if (opts.title) {
        const title = document.createElement('h2');
        title.className = 'admin__modal-title';
        title.textContent = opts.title;
        modal.appendChild(title);
    }
    modal.append(text, error, actions);
    overlay.appendChild(modal);
    ctx.section.appendChild(overlay);
}
